import React, { useState, useEffect } from 'react';
import {
  Settings, Save, RefreshCw, Cpu, MemoryStick, HardDrive, Gauge,
  Clock, Bell, Mail, Bot, AlertTriangle, Shield, Sliders,
  MessageSquare, ChevronRight, User, X, Trash2
} from 'lucide-react';
import { aiAgent, aiAssistantAPI } from '../../../services/api/client';
import { toast } from '../../../utils/toastUtils';
import { useI18n } from '../../../i18n';

const AIAgentConfig = ({ config, onUpdate }) => {
  const { t } = useI18n();
  const [localConfig, setLocalConfig] = useState(config || {});
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Sync localConfig when config prop changes (e.g. after parent refresh)
  useEffect(() => {
    if (config) setLocalConfig(config);
  }, [config]);

  // Conversations state
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    setLoadingConversations(true);
    try {
      const response = await aiAssistantAPI.listConversations({ limit: 50 });
      setConversations(Array.isArray(response?.conversations) ? response.conversations : []);
    } catch (err) {
      console.error('Error loading conversations:', err);
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversationMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const response = await aiAssistantAPI.getConversation(conversationId);
      setConversationMessages(response.messages || []);
      setSelectedConversation(response);
    } catch (err) {
      toast.error(t('aiAgent.loadMessagesError'));
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    if (!window.confirm(t('aiAgent.deleteConversationConfirm'))) return;
    try {
      await aiAssistantAPI.deleteConversation(conversationId);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setConversationMessages([]);
      }
      toast.success(t('aiAgent.conversationDeleted'));
    } catch (err) {
      toast.error(t('aiAgent.conversationDeleteError'));
    }
  };

  const handleChange = (key, value) => {
    setLocalConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await aiAgent.updateConfig(localConfig);
      toast.success(t('aiAgent.configSaved'));
      onUpdate?.();
    } catch (err) {
      toast.error(t('aiAgent.configSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(t('aiAgent.configResetConfirm'))) return;
    setResetting(true);
    try {
      await aiAgent.resetConfig();
      toast.success(t('aiAgent.configReset'));
      onUpdate?.();
    } catch (err) {
      toast.error(t('aiAgent.configResetError'));
    } finally {
      setResetting(false);
    }
  };

  // Slider component
  const ThresholdSlider = ({ label, icon: Icon, value, warningKey, criticalKey, color }) => (
    <div className="bg-brand-black rounded-xl p-4 border border-gray-800">
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-5 h-5 ${color}`} />
        <span className="text-white font-medium">{label}</span>
      </div>
      <div className="space-y-4">
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-orange-400">{t('aiAgent.alert')}</span>
            <span className="text-orange-400">{localConfig[warningKey] || 80}%</span>
          </div>
          <input
            type="range"
            min="50"
            max="95"
            value={localConfig[warningKey] || 80}
            onChange={(e) => handleChange(warningKey, parseInt(e.target.value))}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
          />
        </div>
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-red-400">{t('aiAgent.critical')}</span>
            <span className="text-red-400">{localConfig[criticalKey] || 90}%</span>
          </div>
          <input
            type="range"
            min="60"
            max="99"
            value={localConfig[criticalKey] || 90}
            onChange={(e) => handleChange(criticalKey, parseInt(e.target.value))}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-500"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-brand-green" />
            {t('aiAgent.configTitle')}
          </h3>
          <p className="text-gray-400 text-sm mt-1">{t('aiAgent.configSubtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            disabled={resetting}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
          >
            <RefreshCw className={`w-4 h-4 ${resetting ? 'animate-spin' : ''}`} />
            {t('aiAgent.reset')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/90"
          >
            <Save className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
            {t('aiAgent.save')}
          </button>
        </div>
      </div>

      {/* General Settings */}
      <div className="bg-brand-black rounded-xl p-6 border border-gray-800">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <Sliders className="w-4 h-4 text-brand-green" />
          {t('aiAgent.generalSettings')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="flex items-center gap-3 p-3 bg-brand-black-lighter rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={localConfig.enabled ?? true}
              onChange={(e) => handleChange('enabled', e.target.checked)}
              className="w-5 h-5 rounded accent-brand-green"
            />
            <div>
              <span className="text-white">{t('aiAgent.agentActive')}</span>
              <p className="text-xs text-gray-500">{t('aiAgent.automaticMonitoring')}</p>
            </div>
          </label>
          <label className="flex items-center gap-3 p-3 bg-brand-black-lighter rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={localConfig.auto_recovery_enabled ?? true}
              onChange={(e) => handleChange('auto_recovery_enabled', e.target.checked)}
              className="w-5 h-5 rounded accent-brand-green"
            />
            <div>
              <span className="text-white">{t('aiAgent.autoRecovery')}</span>
              <p className="text-xs text-gray-500">{t('aiAgent.automaticRecovery')}</p>
            </div>
          </label>
          <label className="flex items-center gap-3 p-3 bg-brand-black-lighter rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={localConfig.daily_report_enabled ?? true}
              onChange={(e) => handleChange('daily_report_enabled', e.target.checked)}
              className="w-5 h-5 rounded accent-brand-green"
            />
            <div>
              <span className="text-white">{t('aiAgent.dailyReport')}</span>
              <p className="text-xs text-gray-500">{t('aiAgent.summaryEmail')}</p>
            </div>
          </label>
        </div>
      </div>

      {/* Thresholds */}
      <div className="bg-brand-black rounded-xl p-6 border border-brand-green/30">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-400" />
          {t('aiAgent.alertThresholds')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <ThresholdSlider
            label="CPU"
            icon={Cpu}
            warningKey="cpu_threshold_warning"
            criticalKey="cpu_threshold_critical"
            color="text-brand-green"
          />
          <ThresholdSlider
            label={t('aiAgent.memory')}
            icon={MemoryStick}
            warningKey="memory_threshold_warning"
            criticalKey="memory_threshold_critical"
            color="text-blue-400"
          />
          <ThresholdSlider
            label="GPU"
            icon={Gauge}
            warningKey="gpu_threshold_warning"
            criticalKey="gpu_threshold_critical"
            color="text-purple-400"
          />
          <ThresholdSlider
            label={t('aiAgent.disk')}
            icon={HardDrive}
            warningKey="disk_threshold_warning"
            criticalKey="disk_threshold_critical"
            color="text-orange-400"
          />
        </div>
      </div>

      {/* Intervals */}
      <div className="bg-brand-black rounded-xl p-6 border border-gray-800">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-400" />
          {t('aiAgent.intervals')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.monitoringSeconds')}</label>
            <input
              type="number"
              min="10"
              max="300"
              value={localConfig.monitoring_interval || 30}
              onChange={(e) => handleChange('monitoring_interval', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.aiAnalysisSeconds')}</label>
            <input
              type="number"
              min="60"
              max="3600"
              value={localConfig.analysis_interval || 300}
              onChange={(e) => handleChange('analysis_interval', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.healthCheckSeconds')}</label>
            <input
              type="number"
              min="10"
              max="600"
              value={localConfig.health_check_interval || 60}
              onChange={(e) => handleChange('health_check_interval', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white"
            />
          </div>
        </div>
      </div>

      {/* Scan Settings */}
      <div className="bg-brand-black rounded-xl p-6 border border-gray-800">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <Shield className="w-4 h-4 text-brand-green" />
          {t('aiAgent.scanSettings')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.scanTimeoutMin')}</label>
            <input
              type="number"
              min="5"
              max="1440"
              value={localConfig.scan_timeout_minutes || 60}
              onChange={(e) => handleChange('scan_timeout_minutes', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.maxConcurrentScans')}</label>
            <input
              type="number"
              min="1"
              max="20"
              value={localConfig.max_concurrent_scans || 5}
              onChange={(e) => handleChange('max_concurrent_scans', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.errorRateThreshold')}</label>
            <input
              type="number"
              min="1"
              max="100"
              value={localConfig.error_rate_threshold || 10}
              onChange={(e) => handleChange('error_rate_threshold', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white"
            />
          </div>
        </div>
      </div>

      {/* Alerts Settings */}
      <div className="bg-brand-black rounded-xl p-6 border border-gray-800">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <Bell className="w-4 h-4 text-yellow-400" />
          {t('aiAgent.alerts')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.alertEmail')}</label>
            <input
              type="email"
              value={localConfig.alert_email || ''}
              onChange={(e) => handleChange('alert_email', e.target.value)}
              placeholder="admin@exemple.fr"
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white placeholder-gray-400"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.maxAlertsHour')}</label>
            <input
              type="number"
              min="1"
              max="100"
              value={localConfig.max_alerts_per_hour || 50}
              onChange={(e) => handleChange('max_alerts_per_hour', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.deduplication')}</label>
            <input
              type="number"
              min="1"
              max="60"
              value={localConfig.alert_dedup_minutes || 15}
              onChange={(e) => handleChange('alert_dedup_minutes', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white"
            />
          </div>
        </div>
      </div>

      {/* AI Models */}
      <div className="bg-brand-black rounded-xl p-6 border border-gray-800">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <Bot className="w-4 h-4 text-purple-400" />
          {t('aiAgent.aiModels')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.primaryModel')}</label>
            <input
              type="text"
              value={localConfig.preferred_model || 'sylink/sylink:32b'}
              onChange={(e) => handleChange('preferred_model', e.target.value)}
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.strategicModel')}</label>
            <input
              type="text"
              value={localConfig.preferred_model_strategic || ''}
              onChange={(e) => handleChange('preferred_model_strategic', e.target.value)}
              placeholder="Model for strategic decisions"
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white placeholder-gray-400"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.technicalModel')}</label>
            <input
              type="text"
              value={localConfig.preferred_model_technical || ''}
              onChange={(e) => handleChange('preferred_model_technical', e.target.value)}
              placeholder="Model for technical analyses"
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white placeholder-gray-400"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.codeModel')}</label>
            <input
              type="text"
              value={localConfig.preferred_model_code || ''}
              onChange={(e) => handleChange('preferred_model_code', e.target.value)}
              placeholder="Model for code generation"
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white placeholder-gray-400"
            />
          </div>
        </div>
      </div>

      {/* Daily Report Settings */}
      <div className="bg-brand-black rounded-xl p-6 border border-gray-800">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <Mail className="w-4 h-4 text-blue-400" />
          {t('aiAgent.dailyReportSettings')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.sendTime')}</label>
            <input
              type="number"
              min="0"
              max="23"
              value={localConfig.daily_report_hour || 8}
              onChange={(e) => handleChange('daily_report_hour', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white"
            />
            <p className="text-xs text-gray-500 mt-1">{t('aiAgent.utcNote')}</p>
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('aiAgent.recipients')}</label>
            <textarea
              value={(localConfig.daily_report_recipients || []).join('\n')}
              onChange={(e) => handleChange('daily_report_recipients', e.target.value.split('\n').filter(Boolean))}
              placeholder="admin@exemple.fr"
              rows={3}
              className="w-full px-3 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white placeholder-gray-400"
            />
          </div>
        </div>
      </div>

      {/* AI Conversations History */}
      <div className="bg-brand-black rounded-xl p-6 border border-brand-green/30">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-white font-medium flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-brand-green" />
            {t('aiAgent.conversationHistory')}
          </h4>
          <button
            onClick={loadConversations}
            className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${loadingConversations ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Conversations List */}
          <div className="bg-brand-black-lighter rounded-lg border border-gray-700 max-h-96 overflow-y-auto">
            {loadingConversations ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 text-brand-green animate-spin" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('aiAgent.noConversations')}
              </div>
            ) : (
              <div className="divide-y divide-gray-700">
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => loadConversationMessages(conv.id)}
                    className={`p-3 cursor-pointer hover:bg-gray-700/50 transition-colors ${
                      selectedConversation?.id === conv.id ? 'bg-brand-green/10 border-l-2 border-brand-green' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Bot className="w-4 h-4 text-brand-green flex-shrink-0" />
                        <span className="text-white text-sm truncate">
                          {conv.title || t('aiAgent.untitledConversation')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {conv.message_count || 0} msg
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteConversation(conv.id);
                          }}
                          className="p-1 text-gray-500 hover:text-red-400 rounded"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {conv.updated_at ? new Date(conv.updated_at).toLocaleString() : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Messages Panel */}
          <div className="bg-brand-black-lighter rounded-lg border border-gray-700 max-h-96 overflow-y-auto">
            {loadingMessages ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 text-brand-green animate-spin" />
              </div>
            ) : !selectedConversation ? (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                {t('aiAgent.selectConversation')}
              </div>
            ) : (
              <div className="p-4">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-700">
                  <h5 className="text-white font-medium truncate">
                    {selectedConversation.title || 'Conversation'}
                  </h5>
                  <button
                    onClick={() => {
                      setSelectedConversation(null);
                      setConversationMessages([]);
                    }}
                    className="p-1 text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3">
                  {conversationMessages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`rounded-lg p-3 ${
                        msg.role === 'user'
                          ? 'bg-blue-900/30 border border-blue-800/50 ml-4'
                          : 'bg-gray-800/50 border border-gray-700 mr-4'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {msg.role === 'user' ? (
                          <User className="w-3 h-3 text-blue-400" />
                        ) : (
                          <Bot className="w-3 h-3 text-brand-green" />
                        )}
                        <span className={`text-xs font-medium ${
                          msg.role === 'user' ? 'text-blue-400' : 'text-brand-green'
                        }`}>
                          {msg.role === 'user' ? 'Utilisateur' : 'Assistant IA'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString('fr-FR') : ''}
                        </span>
                      </div>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap break-words">
                        {msg.content?.substring(0, 500)}
                        {msg.content?.length > 500 && '...'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAgentConfig;
